import { Loader } from '@googlemaps/js-api-loader';
import { useTranslation } from 'next-i18next';
import { useState, useEffect, ChangeEvent, createRef, RefObject, Dispatch, SetStateAction } from 'react';
import Link from 'next/link';
import Filters from '@features/showrooms/sections/ShowroomsSection/Components/Filters/Filters';
import ShowroomsList from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomsList/ShowroomsList';
import ShowroomsAccordionList from '@features/showrooms/sections/ShowroomsSection/Components/ShowroomsAccordionList/ShowroomsAccordionList';
import LogoBar from '@features/showrooms/sections/ShowroomsSection/Components/LogoBar/LogoBar';
import styles from '@features/showrooms/sections/ShowroomsSection/Components/SearchShowroom/SearchShowroom.styles';
import { IPointOfPresence, IUserLocation, IGoogleMapLocation, IShoowroomFilters, IPointOfPresenceGroupNames } from '@features/checkout/typescript';
import Heading from '@components/Heading/Heading';
import Button from '@components/Button/Button';
import Icon from '@components/icons/Icon';
import Bookmarks from '@features/showrooms/sections/ShowroomsSection/Components/Bookmarks/Bookmarks';
import { getUserGeolocation, getUserAddress } from '@utils/showroom';
let autocomplete: google.maps.places.Autocomplete | null;
let autocompleteEvent: google.maps.MapsEventListener | null;
let searchTimeOut: NodeJS.Timeout | null;
type PropsType = {
  loader: Loader;
  userLocation: IUserLocation;
  setUserLocation: Dispatch<SetStateAction<IUserLocation>>;
  showrooms: Array<IPointOfPresence>;
  searchShowrooms: (searchWord: string | null, location?: IGoogleMapLocation | null, map?: google.maps.Map | null) => void;
  getClearUserLocation: () => IUserLocation;
  startView: boolean;
  activeFilters: boolean;
  setActiveFilters: Dispatch<SetStateAction<boolean>>;
  filters: IShoowroomFilters;
  handleFilters: (filters: IShoowroomFilters) => void;
  info: string;
  setInfo: Dispatch<SetStateAction<string>>;
  setActiveShowroomFiltersInfo: Dispatch<SetStateAction<boolean>>;
  setActiveServiceFiltersInfo: Dispatch<SetStateAction<boolean>>;
  setShowroomUuid: (newShowroomUuid: string) => void;
  activeBookmarksIndex: number;
  setActiveBookmarksIndex: Dispatch<SetStateAction<number>>;
  isDesktop: boolean;
  showroomsLoaded: boolean;
  map: google.maps.Map | null;
  useShowroomsFilters: boolean;
  showroomsNames: IPointOfPresenceGroupNames;
  showPartnersLogos: boolean;
};
const SearchShowroom = ({
  loader,
  userLocation,
  setUserLocation,
  showrooms,
  searchShowrooms,
  getClearUserLocation,
  startView,
  activeFilters,
  setActiveFilters,
  filters,
  handleFilters,
  info,
  setInfo,
  setActiveShowroomFiltersInfo,
  setActiveServiceFiltersInfo,
  setShowroomUuid,
  activeBookmarksIndex,
  setActiveBookmarksIndex,
  isDesktop,
  showroomsLoaded,
  map,
  useShowroomsFilters,
  showroomsNames,
  showPartnersLogos
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const searchInput: RefObject<HTMLInputElement | null> = createRef();
  const [autoFocus, setAutoFocus] = useState<boolean>(true);
  const [askUserLocation, setAskUserLocation] = useState<boolean>(false);
  const [iconCloseIsVisible, setIconCloseIsVisible] = useState<boolean>(false);
  const autocompleteOptions = {
    types: ['(regions)'],
    fields: ['address_components', 'geometry']
  };
  const clearTimeOut = () => {
    if (searchTimeOut) {
      clearTimeout(searchTimeOut);
    }
  };
  const handleIconCloseIsVisible = () => {
    if (searchInput?.current) {
      setIconCloseIsVisible(searchInput?.current.value.length > 0);
    }
  };
  const handleClearInput = () => {
    if (searchInput?.current) {
      searchInput.current.value = '';
      searchShowrooms('');
      handleIconCloseIsVisible();
    }
  };
  const handleChange = (newSearchWord: string) => {
    clearTimeOut();
    handleIconCloseIsVisible();
    searchTimeOut = setTimeout(() => {
      searchShowrooms(newSearchWord);
    }, 3000);
  };
  const handleSearchInputAutocomplete = () => {
    if (searchInput?.current) {
      if (!autocomplete) autocomplete = new google.maps.places.Autocomplete(searchInput.current, autocompleteOptions);
      if (autocompleteEvent) google.maps.event.removeListener(autocompleteEvent);
      autocompleteEvent = autocomplete.addListener('place_changed', () => {
        if (autocomplete) {
          const place = autocomplete.getPlace();
          if (place?.geometry?.location) {
            clearTimeOut();
            searchShowrooms(null, {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            });
          }
        }
      });
    }
  };
  const setSearchPositionByUserPosition = (newUserLocation: IUserLocation) => {
    if (newUserLocation.location.lat && newUserLocation.location.lng) {
      searchShowrooms(null, {
        lat: newUserLocation.location.lat,
        lng: newUserLocation.location.lng
      });
    }
  };
  const handleGeolocationUnsuccessful = () => {
    setInfo(t('geolocationUnsuccessful'));
  };
  const setUserCityAndCountry = (newUserLocation: IUserLocation) => {
    if (newUserLocation.city && newUserLocation.country && searchInput?.current) {
      searchInput.current.value = `${newUserLocation.city}, ${newUserLocation.country}`;
    }
  };
  const handleLocalization = async () => {
    if (userLocation.city && userLocation.location.lat && userLocation.location.lng) {
      setUserCityAndCountry(userLocation);
      setSearchPositionByUserPosition(userLocation);
      handleIconCloseIsVisible();
    } else {
      const newUserGeolocation = await getUserGeolocation();
      const newUserAddress = await getUserAddress({
        ...userLocation,
        location: newUserGeolocation
      });
      if (newUserAddress.city && newUserAddress.country) {
        setUserCityAndCountry(newUserAddress);
        setSearchPositionByUserPosition(newUserAddress);
        setUserLocation(getClearUserLocation());
        setUserLocation(newUserAddress);
        handleIconCloseIsVisible();
      } else {
        handleGeolocationUnsuccessful();
      }
    }
  };
  const handleUserLocation = async () => {
    if (!askUserLocation) {
      const newUserLocation = {
        ...userLocation
      };
      const newUserGeolocation = await getUserGeolocation();
      if (newUserGeolocation?.lat && newUserGeolocation?.lng) {
        newUserLocation.location = newUserGeolocation;
      }
      const newUserAddress = await getUserAddress({
        ...userLocation,
        location: newUserGeolocation
      });
      if (newUserAddress?.city) {
        newUserLocation.city = newUserAddress.city;
      }
      if (newUserAddress?.country) {
        newUserLocation.country = newUserAddress.country;
      }
      setUserLocation(newUserLocation);
      setAskUserLocation(true);
      handleIconCloseIsVisible();
    }
  };
  const onResize = () => {
    if (window.innerWidth < 1600) {
      setAutoFocus(false);
    } else {
      setAutoFocus(true);
    }
  };
  useEffect(() => {
    loader.load().then(() => {
      handleUserLocation();
      handleSearchInputAutocomplete();
    });
    if (autoFocus && searchInput?.current && startView && !userLocation?.city) {
      searchInput.current.focus();
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      if (autocompleteEvent) google.maps.event.removeListener(autocompleteEvent);
      autocompleteEvent = null;
      autocomplete = null;
      clearTimeOut();
      window.removeEventListener('resize', onResize);
    };
  });
  useEffect(() => {
    if (showrooms?.length && searchInput?.current && showroomsLoaded && map) {
      const newSearchWord = searchInput?.current.value;
      if (newSearchWord.length) {
        searchShowrooms(newSearchWord, null, map);
      }
    }
  }, [showroomsLoaded, map]);
  return <div sx={styles.container}>
      <Heading h={1} size={20} uppercase pb={24} mobile>
        {t('showroomMap')}
      </Heading>
      <div sx={styles.searchInputContainer}>
        <div sx={styles.searchIconContainer}>
          <Icon icon="Search" size="20px" />
        </div>
        <input type="text" placeholder={t('searchShowroom')} onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)} ref={searchInput} autoComplete="off" />
        <div sx={styles.navigationButtonContainer}>
          {iconCloseIsVisible ? <Button variant="iconOnly" onClick={handleClearInput}>
              <Icon icon="Close" size="24px" />
            </Button> : <Button variant="iconOnly" onClick={handleLocalization}>
              <Icon icon="LocationSearching" size="24px" />
            </Button>}
        </div>
      </div>
      <div sx={styles.infoContainer}>{info}</div>
      {!isDesktop && !startView && <Heading h={1} headingType={6} pb={16}>
          {t('showroomsFound', {
        quantity: showrooms.length
      })}
        </Heading>}
      {useShowroomsFilters && <Filters active={startView || activeFilters} buttonFilters={!startView} setActive={setActiveFilters} filters={filters} handleFilters={handleFilters} setActiveShowroomFiltersInfo={setActiveShowroomFiltersInfo} setActiveServiceFiltersInfo={setActiveServiceFiltersInfo} showroomsNames={showroomsNames} />}
      {!isDesktop && !startView && <div sx={styles.bookmarks}>
          <Bookmarks activeIndex={activeBookmarksIndex} setActiveIndex={setActiveBookmarksIndex} />
        </div>}
      <ShowroomsList active={isDesktop && !startView || !isDesktop && activeBookmarksIndex === 1} isDesktop={isDesktop} small={activeFilters && isDesktop} showrooms={showrooms} setShowroomUuid={setShowroomUuid} />
      <ShowroomsAccordionList active={startView && !useShowroomsFilters} isDesktop={isDesktop} showrooms={showrooms} setShowroomUuid={setShowroomUuid} />
      {showPartnersLogos && (startView || !isDesktop) && <div sx={styles.logoBarContainer}>
          <div sx={styles.logoBarContainer.bottomLinkContainer}>
            <Link href="/showrooms/list">{t('seeListWithShowrooms')}</Link>
          </div>
          <LogoBar />
        </div>}
    </div>;
};
export default SearchShowroom;